// src/app/router.tsx
import {
  Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect,
} from "@tanstack/react-router";
import type { ComponentType } from "react";
import { z } from "zod";

import { session } from "@/auth/session";
import { EmptyState, RouteSkeleton } from "@/design-system";

import { AppShell } from "./AppShell";
import { appPages } from "./routes";

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => <div className="app-bg min-h-screen p-10"><EmptyState title="الصفحة غير موجودة" hint="تحقق من الرابط أو عد إلى لوحة القيادة." /></div>,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    throw redirect({ to: (await session.ensure()) ? "/dashboard" : "/login" });
  },
});

const publicSearch = z.object({ redirect: z.string().optional(), registered: z.string().optional() });
const publicPage = (path: string, load: () => Promise<{ default: ComponentType }>) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    validateSearch: publicSearch,
    beforeLoad: async () => {
      if (await session.ensure()) throw redirect({ to: "/dashboard" });
    },
    component: lazyRouteComponent(load),
  });

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppShell,
  beforeLoad: async ({ location }) => {
    if (!(await session.ensure())) throw redirect({ to: "/login", search: { redirect: location.pathname } });
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  publicPage("/login", () => import("@/features/auth/LoginPage")),
  publicPage("/register", () => import("@/features/auth/RegisterPage")),
  appRoute.addChildren(appPages(appRoute)),
]);

// الزر يستجيب فوراً دايماً (تغيير الرابط متزامن) — اللي كان بيحس "تجمد" هو غياب أي مؤشر
// انتظار: الراوتر بيستنى beforeLoad + استيراد حزمة الصفحة كاملين قبل ما يعرض أي حاجة
// (pendingMs الافتراضي 1000ms ومفيش pendingComponent أصلاً). هنا بنوريه فوراً (0ms) لأي
// انتقال بياخد وقت، وبأقل مدة عرض (150ms) عشان مايومضش لو الصفحة جاهزة بالفعل.
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPendingComponent: RouteSkeleton,
  defaultPendingMs: 0,
  defaultPendingMinMs: 150,
});
